import { useMemo, useState } from 'react';
import { useOpsPage } from '../hooks/useDailyInstallations';
import {
  BRAND, BarRow, DayChart, Note, PageHeader, Panel, States, Stat,
  dec, mins, monthName, num, pct, selectClass, thisMonth,
} from '../components/OpsPageKit';

// Installation Master — the NEO2 rebuild.
//
// Booked against completed, the completion rate between them, and the month-end
// projection. The projection deliberately runs off complete days only: including
// a part-finished today would drag the run rate down every morning.
function InstallationMaster() {
  const [month, setMonth] = useState(thisMonth());
  const [region, setRegion] = useState('');
  // Region B / Region N (stored as BRSK / Netomnia); `region` above is the area.
  const [contract, setContract] = useState('');

  const filters = useMemo(() => ({ month, region, contract }), [month, region, contract]);
  const {
    data, isLoading, isFetching, refetch, isError,
  } = useOpsPage('installation-master', filters);

  const t = data?.totals || {};
  const wd = data?.working_days || {};
  const cov = data?.coverage || {};
  const regions = (data?.by_region || []).map((r) => r.region);
  const maxRegion = Math.max(1, ...(data?.by_region || []).map((r) => r.booked));

  return (
    <div className="p-6">
      <PageHeader
        title="Installation Master"
        subtitle="Jobs booked against jobs completed, and where the month lands."
        month={month}
        onMonth={setMonth}
        onRefresh={refetch}
        fetching={isFetching}
      >
        <select value={contract} onChange={(e) => { setContract(e.target.value); setRegion(''); }} className={selectClass} aria-label="Region">
          <option value="">Both regions</option>
          <option value="BRSK">Region B</option>
          <option value="NETOMNIA">Region N</option>
        </select>
        <select value={region} onChange={(e) => setRegion(e.target.value)} className={selectClass} aria-label="Area">
          <option value="">All areas</option>
          {regions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </PageHeader>

      <States isLoading={isLoading} isError={isError} what="installation master" />

      {data && (
        <>
          {cov.partial_month ? (
            <Note tone="red">
              <strong>{monthName(month)} is only partly covered.</strong>{' '}
              The installations mirror starts on {new Date(cov.data_from).toLocaleDateString('en-GB')},
              so everything below counts part of the month.
            </Note>
          ) : null}

          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat label="Booked" value={num(t.booked)} sub="installation work" accent />
            <Stat label="Completed" value={num(t.completed)} sub={`of ${num(t.jobs)} jobs`} />
            <Stat
              label="Completion rate"
              value={pct(t.completion_rate)}
              sub="completed ÷ booked"
              tone={t.completion_rate >= 0.8 ? '#16A34A' : '#D97706'}
            />
            <Stat
              label="Projected"
              value={num(t.projected)}
              sub={`${num(t.installs_mtd)} to ${String(wd.cutoff).split("-").reverse().join("/")} ÷ ${wd.so_far} days × ${wd.total}`}
              accent
            />
            <Stat label="Average install" value={mins(t.avg_minutes)} sub={`${num(t.total_hours)} hours total`} />
          </div>

          {/* The FNO forecast locked on the FDC page, for the same region / area. */}
          {t.fno_forecast === null ? (
            <p className="mt-3 text-xs text-gray-400">No FNO forecast has been locked on the FDC page for {monthName(month)} yet.</p>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-3">
              <Stat label="FNO forecast" value={num(t.fno_forecast)} sub="locked on the FDC page" accent />
              <Stat
                label="Completed of FNO"
                value={t.fno_pct_complete === null ? '—' : pct(t.fno_pct_complete)}
                sub={`${num(t.completed)} of ${num(t.fno_forecast)}`}
                tone={t.fno_pct_complete >= 1 ? '#16A34A' : undefined}
              />
              <Stat
                label="Projected vs FNO"
                value={`${t.fno_projected_gap > 0 ? '+' : ''}${num(t.fno_projected_gap)}`}
                sub={`${num(t.projected)} projected against ${num(t.fno_forecast)}`}
                tone={t.fno_projected_gap >= 0 ? '#16A34A' : '#DC2626'}
              />
            </div>
          )}

          {(data.fno_by_region || []).length ? (
            <div className="mt-4">
              <Panel title="Against the FNO forecast">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-3 py-2">FDC region</th>
                        <th className="px-3 py-2 text-right">FNO</th>
                        <th className="px-3 py-2 text-right">Completed</th>
                        <th className="px-3 py-2 text-right">Projected</th>
                        <th className="px-3 py-2 text-right">vs FNO</th>
                        <th className="w-1/4 px-3 py-2">% of FNO</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.fno_by_region.map((r) => (
                        <tr key={r.region} className="hover:bg-gray-50">
                          <td className="px-3 py-1.5 text-gray-800">
                            {r.region}
                            {r.areas.length && r.areas.join(' + ') !== r.region.replace(/^Region [BN] /, '') ? (
                              <div className="text-[11px] text-gray-400">{`jobs: ${r.areas.join(' + ')}`}</div>
                            ) : null}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">{num(r.fno)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">{num(r.completed)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">{num(r.projected)}</td>
                          <td className={`px-3 py-1.5 text-right font-semibold tabular-nums ${r.projected_gap >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                            {`${r.projected_gap > 0 ? '+' : ''}${num(r.projected_gap)}`}
                          </td>
                          <td className="px-3 py-1.5">
                            {r.pct_complete === null ? (
                              <span className="text-xs text-gray-400">no FNO set</span>
                            ) : (
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                                  <div className="h-full rounded-full" style={{ width: `${Math.min(r.pct_complete, 1) * 100}%`, background: BRAND }} />
                                </div>
                                <span className="w-10 text-right text-xs tabular-nums text-gray-500">{pct(r.pct_complete)}</span>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </div>
          ) : null}

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Booked and completed by day">
              <DayChart
                rows={data.by_day}
                series={[
                  { key: 'completed', label: 'Completed', colour: BRAND },
                  { key: 'booked', label: 'Booked', colour: '#E9D8F0' },
                ]}
              />
            </Panel>

            <Panel title="By region">
              <div className="py-1">
                {data.by_region.map((r) => (
                  <BarRow
                    key={r.region}
                    label={r.region}
                    value={r.booked}
                    total={maxRegion}
                    sub={`${pct(r.rate)} done`}
                  />
                ))}
              </div>
            </Panel>
          </div>

          <div className="mt-4">
            <Panel title="Install status">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2 text-right">Jobs</th>
                      <th className="px-3 py-2 text-right">Share</th>
                      <th className="px-3 py-2 w-1/3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.statuses.map((s) => (
                      <tr key={s.status} className="hover:bg-gray-50">
                        <td className="px-3 py-1.5 text-gray-800">{s.status}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">{num(s.n)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{pct(s.pct)}</td>
                        <td className="px-3 py-1.5">
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${s.pct * 100}%`, background: BRAND }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>

          <Note>
            The projection extrapolates {num(t.installs_mtd)} completions over{' '}
            {wd.so_far} complete working days across all {wd.total} in the month — today is excluded
            so a part-finished day never drags the rate down. Bank holidays are not deducted:
            the report used a holiday table generated inside the model, which has no source we hold.
            Duration is known for {num(t.with_times)} of {num(t.completed)} completions
            ({dec((t.with_times / Math.max(t.completed, 1)) * 100, 0)}%), and the average covers
            only those.
          </Note>
        </>
      )}
    </div>
  );
}

export default InstallationMaster;
